import React, { useState, useEffect } from 'react';
import { MdClose, MdBusiness, MdPerson, MdLocationOn } from 'react-icons/md';
import axios from 'axios';
import { API_BASE_URL, API_CONFIG } from '../../config/api';

const AddTraderModal = ({ isOpen, onClose, showPopup, fetchData, userMmdaId, user, onSuccess, onError }) => {
  const [formData, setFormData] = useState({
    business_name: '',
    first_name: '',
    middle_name: '',
    last_name: '',
    phone: '',
    countryCode: '+233',
    email: '',
    business_type: 'Retail',
    zone_id: '',
    group_id: '',
    id_type: '',
    national_info: '',
    location: '',
    registration_date: new Date().toISOString().split('T')[0],
  });
  const [zones, setZones] = useState([]);
  const [groups, setGroups] = useState([]);
  const [idTypes, setIdTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [groupsLoading, setGroupsLoading] = useState(false);



  // Use the user prop passed from parent component

  // Fetch zones and ID types for dropdown
  const fetchDropdownData = async () => {
    setDataLoading(true);
    try {
      const [zonesRes, idTypesRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/zones?userRole=MMDA&mmda_id=${userMmdaId}`, API_CONFIG),
        axios.get(`${API_BASE_URL}/national-ids`, API_CONFIG)
      ]);

      setZones(zonesRes.data);
      setIdTypes(idTypesRes.data);
    } catch (error) {

    } finally {
      setDataLoading(false);
    }
  };

  // Fetch groups for a specific zone
  const fetchGroupsForZone = async (zoneId) => {
    if (!zoneId) {
      setGroups([]);
      return;
    }

    setGroupsLoading(true);
    try {
      const url = `${API_BASE_URL}/zonal-groups?userRole=MMDA&mmda_id=${userMmdaId}&zone_id=${zoneId}`;
      const groupsRes = await axios.get(url, API_CONFIG);
      setGroups(groupsRes.data);
    } catch (error) {

      setGroups([]);
    } finally {
      setGroupsLoading(false);
    }
  };

  // Handle zone change to fetch groups
  useEffect(() => {
    if (formData.zone_id) {
      fetchGroupsForZone(formData.zone_id);
    } else {
      setGroups([]);
    }
  }, [formData.zone_id]);

  useEffect(() => {
    if (isOpen) {
      fetchDropdownData();
      // Clear groups when modal opens
      setGroups([]);
    }
  }, [isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // If zone is changing, clear the group selection
    if (name === 'zone_id') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        group_id: '' // Clear group when zone changes
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Combine country code with phone number for database storage
      const fullPhoneNumber = formData.phone ? `${formData.countryCode}${formData.phone}` : '';

      const traderData = {
        ...formData,
        phone: fullPhoneNumber, // Store the full phone number with country code
        mmda_id: userMmdaId,
        status: 'active',
        created_by: user?.user_id, // Use current user ID or fallback
        // Combine first, middle, last names into owner_name for backward compatibility
        owner_name: `${formData.first_name} ${formData.middle_name ? formData.middle_name + ' ' : ''}${formData.last_name}`.trim()
      };

      // Remove countryCode from the data sent to backend as it's now included in phone
      delete traderData.countryCode;

      const response = await axios.post(`${API_BASE_URL}/traders`, traderData, API_CONFIG);

      if (response.status === 201) {
        setFormData({
          business_name: '',
          first_name: '',
          middle_name: '',
          last_name: '',
          phone: '',
          countryCode: '+233',
          email: '',
          business_type: 'Retail',
          zone_id: '',
          group_id: '',
          id_type: '',
          national_info: '',
          location: '',
          registration_date: new Date().toISOString().split('T')[0],
        });
        setGroups([]); // Clear groups
        onClose();
        
        // Show success modal
        if (onSuccess) {
          onSuccess('Registered', 'Trader', formData.business_name, 'Business has been successfully registered and can now operate within the assigned zone.');
        } else {
          showPopup('Trader registered successfully!', 'success');
          fetchData();
        }
      }
    } catch (error) {

      
      // Show error modal
      if (onError) {
        onError('Register', 'Trader', formData.business_name, error.response?.data?.error || error.message);
      } else {
        showPopup(error.response?.data?.error || 'Failed to register trader', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      business_name: '',
      first_name: '',
      middle_name: '',
      last_name: '',
      phone: '',
      countryCode: '+233',
      email: '',
      business_type: 'Retail',
      zone_id: '',
      group_id: '',
      id_type: '',
      national_info: '',
      location: '',
      registration_date: new Date().toISOString().split('T')[0],
    });
    setGroups([]); // Clear groups
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: 12,
        padding: 24,
        width: '90%',
        maxWidth: 600,
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 20,
          paddingBottom: 16,
          borderBottom: '1px solid #e0e0e0',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <MdBusiness size={24} style={{ color: '#28a745' }} />
            <h2 style={{ margin: 0, color: '#333', fontSize: 20 }}>Register New Trader</h2>
          </div>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 8,
              borderRadius: 4,
            }}
          >
            <MdClose size={24} style={{ color: '#666' }} />
          </button>
        </div>

        {dataLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <p>Loading form data...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {/* Business Information */}
            <div style={{ marginBottom: 20 }}>
              <h3 style={{ margin: '0 0 16px 0', color: '#333', fontSize: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                <MdBusiness size={20} style={{ color: '#28a745' }} />
                Business Information
              </h3>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold', fontSize: 14 }}>
                    Business Name *
                  </label>
                  <input
                    type="text"
                    name="business_name"
                    value={formData.business_name}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: '90%',
                      padding: '10px 12px',
                      border: '1px solid #ddd',
                      borderRadius: 6,
                      fontSize: 14,
                      outline: 'none',
                    }}
                    placeholder="Enter business name"
                  />
                </div>
                
                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold', fontSize: 14 }}>
                    Business Type *
                  </label>
                  <select
                    name="business_type"
                    value={formData.business_type}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #ddd',
                      borderRadius: 6,
                      fontSize: 14,
                      outline: 'none',
                    }}
                  >
                    <option value="Retail">Retail</option>
                    <option value="Wholesale">Wholesale</option>
                    <option value="Restaurant">Restaurant</option>
                    <option value="Service">Service</option>
                    <option value="Manufacturing">Manufacturing</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Owner Information */}
            <div style={{ marginBottom: 20 }}>
              <h3 style={{ margin: '0 0 16px 0', color: '#333', fontSize: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                <MdPerson size={20} style={{ color: '#17a2b8' }} />
                Owner Information
              </h3>

              {/* Name Fields */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold', fontSize: 14 }}>
                    First Name *
                  </label>
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: '90%',
                      padding: '10px 12px',
                      border: '1px solid #ddd',
                      borderRadius: 6,
                      fontSize: 14,
                      outline: 'none',
                    }}
                    placeholder="First name"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold', fontSize: 14 }}>
                    Middle Name
                  </label>
                  <input
                    type="text"
                    name="middle_name"
                    value={formData.middle_name}
                    onChange={handleInputChange}
                    style={{
                      width: '90%',
                      padding: '10px 12px',
                      border: '1px solid #ddd',
                      borderRadius: 6,
                      fontSize: 14,
                      outline: 'none',
                    }}
                    placeholder="Middle name (optional)"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold', fontSize: 14 }}>
                    Last Name *
                  </label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: '90%',
                      padding: '10px 12px',
                      border: '1px solid #ddd',
                      borderRadius: 6,
                      fontSize: 14,
                      outline: 'none',
                    }}
                    placeholder="Last name"
                  />
                </div>
              </div>

              {/* Contact and ID Information */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold', fontSize: 14 }}>
                    Phone Number *
                  </label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <select
                      name="countryCode"
                      value={formData.countryCode || '+233'}
                      onChange={handleInputChange}
                      style={{
                        width: '80px',
                        padding: '10px 8px',
                        border: '1px solid #ddd',
                        borderRadius: 6,
                        fontSize: 14,
                        outline: 'none',
                        backgroundColor: 'white',
                      }}
                    >
                      <option value="+233">🇬🇭 +233</option>
                      <option value="+234">🇳🇬 +234</option>
                      <option value="+1">🇺🇸 +1</option>
                      <option value="+44">🇬🇧 +44</option>
                      <option value="+27">🇿🇦 +27</option>
                    </select>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      style={{
                        flex: 1,
                        padding: '10px 12px',
                        border: '1px solid #ddd',
                        borderRadius: 6,
                        fontSize: 14,
                        outline: 'none',
                      }}
                      placeholder="241234567"
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold', fontSize: 14 }}>
                    ID Type
                  </label>
                  <select
                    name="id_type"
                    value={formData.id_type}
                    onChange={handleInputChange}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #ddd',
                      borderRadius: 6,
                      fontSize: 14,
                      outline: 'none',
                    }}
                  >
                    <option value="">Select ID Type (Optional)</option>
                    {idTypes.map(idType => (
                      <option key={idType.id} value={idType.id}>
                        {idType.id_type}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Email and National Info */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold', fontSize: 14 }}>
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    style={{
                      width: '90%',
                      padding: '10px 12px',
                      border: '1px solid #ddd',
                      borderRadius: 6,
                      fontSize: 14,
                      outline: 'none',
                    }}
                    placeholder="owner@business.com (optional)"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold', fontSize: 14 }}>
                    National ID/Info
                  </label>
                  <input
                    type="text"
                    name="national_info"
                    value={formData.national_info}
                    onChange={handleInputChange}
                    style={{
                      width: '90%',
                      padding: '10px 12px',
                      border: '1px solid #ddd',
                      borderRadius: 6,
                      fontSize: 14,
                      outline: 'none',
                    }}
                    placeholder="ID number (optional)"
                  />
                </div>
              </div>
            </div>

            {/* Location Information */}
            <div style={{ marginBottom: 20 }}>
              <h3 style={{ margin: '0 0 16px 0', color: '#333', fontSize: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                <MdLocationOn size={20} style={{ color: '#ff9800' }} />
                Location Information
              </h3>
              
              {/* Zone Selection */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold', fontSize: 14 }}>
                  Zone *
                </label>
                <select
                  name="zone_id"
                  value={formData.zone_id}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #ddd',
                    borderRadius: 6,
                    fontSize: 14,
                    outline: 'none',
                  }}
                >
                  <option value="">Select Zone</option>
                  {zones.map(zone => (
                    <option key={zone.zone_id} value={zone.zone_id}>
                      {zone.zone_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Group Selection - Only show when zone is selected */}
              {formData.zone_id && (
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold', fontSize: 14 }}>
                    Group (Optional)
                  </label>
                  <select
                    name="group_id"
                    value={formData.group_id}
                    onChange={handleInputChange}
                    disabled={groupsLoading}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #ddd',
                      borderRadius: 6,
                      fontSize: 14,
                      outline: 'none',
                      backgroundColor: groupsLoading ? '#f5f5f5' : 'white',
                      cursor: groupsLoading ? 'not-allowed' : 'pointer',
                    }}
                  >
                    <option value="">
                      {groupsLoading ? 'Loading groups...' : 'Select a group (optional)'}
                    </option>
                    {!groupsLoading && groups.map(group => (
                      <option key={group.group_id} value={group.group_id}>
                        {group.group_name}
                      </option>
                    ))}
                  </select>
                  {!groupsLoading && groups.length === 0 && formData.zone_id && (
                    <p style={{ margin: '4px 0 0 0', fontSize: 12, color: '#666', fontStyle: 'italic' }}>
                      No groups available for this zone
                    </p>
                  )}
                  {!groupsLoading && groups.length === 0 && !formData.zone_id && (
                    <p style={{ margin: '4px 0 0 0', fontSize: 12, color: '#666', fontStyle: 'italic' }}>
                      Please select a zone first to see available groups
                    </p>
                  )}
                  {groupsLoading && (
                    <p style={{ margin: '4px 0 0 0', fontSize: 12, color: '#ff9800', fontStyle: 'italic' }}>
                      Loading groups for selected zone...
                    </p>
                  )}
                </div>
              )}

              {/* Registration Date */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold', fontSize: 14 }}>
                  Registration Date *
                </label>
                <input
                  type="date"
                  name="registration_date"
                  value={formData.registration_date}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '95%',
                    padding: '10px 12px',
                    border: '1px solid #ddd',
                    borderRadius: 6,
                    fontSize: 14,
                    outline: 'none',
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold', fontSize: 14 }}>
                  Business Location/Address *
                </label>
                <textarea
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  required
                  rows={3}
                  style={{
                    width: '95%',
                    padding: '10px 12px',
                    border: '1px solid #ddd',
                    borderRadius: 6,
                    fontSize: 14,
                    outline: 'none',
                    resize: 'vertical',
                  }}
                  placeholder="Enter detailed business address or location"
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 24 }}>
              <button
                type="button"
                onClick={handleClose}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #6c757d',
                  borderRadius: 6,
                  backgroundColor: 'white',
                  color: '#6c757d',
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 'bold',
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: 6,
                  backgroundColor: loading ? '#6c757d' : '#28a745',
                  color: 'white',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: 14,
                  fontWeight: 'bold',
                }}
              >
                {loading ? 'Registering...' : 'Register Trader'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default AddTraderModal;
